import React, { useEffect, useState, useRef } from 'react';
import { MessageCircle, X, Send, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'agent';
  time: Date;
}
export function LiveChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    text: 'Hi! How can we help you today? Ask us about materials, delivery, or pricing.',
    sender: 'agent',
    time: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      time: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setIsTyping(true);
    // Simulate agent typing and replying
    setTimeout(() => {
      const newAgentMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Thanks for reaching out! One of our team members will be with you shortly. For immediate help, call (206) 619-3742.',
        sender: 'agent',
        time: new Date()
      };
      setMessages(prev => [...prev, newAgentMessage]);
      setIsTyping(false);
    }, 1500);
  };
  return <div className="hidden md:flex fixed bottom-6 right-6 z-50 flex-col items-end" data-id="element-532">
      <AnimatePresence data-id="element-533">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="bg-white dark:bg-[#333333] w-80 sm:w-96 rounded-2xl shadow-2xl border border-brand-lightgray dark:border-[#444444] mb-4 overflow-hidden flex flex-col" style={{
        height: '450px',
        maxHeight: 'calc(100vh - 120px)'
      }} data-id="element-534">
            {/* Header */}
            <div className="bg-brand-charcoal dark:bg-[#141510] p-4 flex justify-between items-center" data-id="element-535">
              <div className="flex items-center" data-id="element-536">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse" data-id="element-537"></div>
                <h3 className="text-white font-heading font-bold" data-id="element-538">
                  Chat with us
                </h3>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-stone-200 hover:text-white transition-colors" data-id="element-539">
                <X className="w-5 h-5" data-id="element-540" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-grow p-4 overflow-y-auto bg-stone-50 dark:bg-[#222222]" data-id="element-541">
              <div className="space-y-4" data-id="element-542">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-543">
                    {msg.sender === 'agent' && <div className="w-8 h-8 rounded-full bg-brand-purple flex items-center justify-center mr-2 flex-shrink-0" data-id="element-544">
                        <User className="w-4 h-4 text-white" data-id="element-545" />
                      </div>}
                    <div className={`max-w-[75%] p-3 rounded-2xl text-sm ${msg.sender === 'user' ? 'bg-brand-orange text-white rounded-br-none' : 'bg-white dark:bg-[#333333] text-brand-charcoal dark:text-stone-200 border border-brand-lightgray dark:border-[#444444] rounded-bl-none shadow-sm'}`} data-id="element-546">
                      {msg.text}
                    </div>
                  </div>)}
                {isTyping && <div className="flex justify-start" data-id="element-547">
                    <div className="w-8 h-8 rounded-full bg-brand-purple flex items-center justify-center mr-2 flex-shrink-0" data-id="element-548">
                      <User className="w-4 h-4 text-white" data-id="element-549" />
                    </div>
                    <div className="bg-white dark:bg-[#333333] border border-brand-lightgray dark:border-[#444444] p-3 rounded-2xl rounded-bl-none shadow-sm flex space-x-1 items-center" data-id="element-550">
                      <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-551"></div>
                      <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-552"></div>
                      <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-553"></div>
                    </div>
                  </div>}
                <div ref={messagesEndRef} data-id="element-554" />
              </div>
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white dark:bg-[#333333] border-t border-brand-lightgray dark:border-[#444444]" data-id="element-555">
              <form onSubmit={handleSend} className="flex gap-2" data-id="element-556">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Type your message..." className="flex-grow px-4 py-2 bg-stone-50 dark:bg-[#222222] border border-brand-lightgray dark:border-stone-700 rounded-full text-sm text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent" data-id="element-557" />
                <button type="submit" disabled={!inputValue.trim()} className="p-2 bg-brand-button hover:bg-brand-buttonHover text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-558">
                  <Send className="w-4 h-4" data-id="element-559" />
                </button>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      <button onClick={() => setIsOpen(!isOpen)} className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 ${isOpen ? 'bg-brand-charcoal dark:bg-stone-800 text-white' : 'bg-[#05894d] text-white'}`} aria-label="Toggle Chat" data-id="element-560">
        {isOpen ? <X className="w-6 h-6" data-id="element-561" /> : <MessageCircle className="w-6 h-6" data-id="element-562" />}
      </button>
    </div>;
}